import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Tooltip,
  Checkbox,
  Drawer,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RootPropsExcelEditor from './RootPropsExcelEditor';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import Settings from '@mui/icons-material/Settings';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ViewColumn from '@mui/icons-material/ViewColumn';
import { AutocompleteDropdown } from './AutocompleteDropdown';
import { useApiClient } from '../../config/axios';
import { AxiosInstance } from 'axios';

// Helper function to determine entity types based on column
const getEntityTypesForColumn = (columnKey: string): string[] => {
  switch (columnKey) {
    case 'data_index':
      return ['attribute', 'level', 'measure', 'member', 'base_metrics', 'computed_metric'];
    case 'xtype':
      return ['xtype'];
    case 'dimension':
      return ['dimension'];
    case 'renderer':
      return ['renderer'];
    case 'editor':
      return ['editor'];
    case 'text':
    case 'label':
    case 'label_text':
    case 'display_text':
      return ['attribute', 'level', 'measure', 'member', 'base_metrics', 'computed_metric'];
    default:
      return ['attribute', 'level', 'measure'];
  }
};

/**
 * Generate a UUID v4
 * This is a simple implementation that generates valid UUIDs for PostgreSQL
 */
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Renumber seq_no so each row's value matches its position among its
 * siblings (rows with the same parent_item_id) in the array. The save
 * endpoint persists seq_no as-is and the load query `ORDER BY parent_item_id,
 * seq_no` — so callers MUST run this after any structural change (move, add,
 * delete) for the in-memory order to survive the round trip.
 */
function renumberSeqNoFlat(arr: any[]): any[] {
  const counters = new Map<any, number>();
  return arr.map(row => {
    const parentKey = row.parent_item_id ?? '__root__';
    const next = counters.get(parentKey) ?? 0;
    counters.set(parentKey, next + 1);
    return { ...row, seq_no: next };
  });
}

/**
 * Free-text → typed value coercion for extra_props.
 *
 * The extra-props editor uses a single TextField that always yields a string.
 * Source viewdefns commonly have booleans (`"editable": false`), numbers, and
 * occasionally nested objects/arrays in extra_props. Without this coercion,
 * a user typing `true` ends up with `"true"` (string) in the DB and the
 * rendered JSON shows the quoted form — Bug 2 (false booleans round-tripped
 * fine in extra_props that came from the pipeline, but user-added values via
 * the editor were always strings).
 *
 * Coercions:
 *   ""              → "" (empty string preserved — user might want an empty
 *                     string value)
 *   "true"/"false"  → boolean
 *   "null"          → null
 *   "1.5", "-3", …  → number (only when the whole string is a valid number)
 *   "{...}"/"[...]" → JSON parse if it round-trips
 *   anything else   → original string
 *
 * Edge case: a literal string "true" is unreachable through this editor. For
 * the configs in scope, that's acceptable — none have a deliberate string
 * value of "true"/"false"/numeric-looking text in extra_props.
 */
function coerceExtraPropValue(s: string): any {
  if (s === '') return '';
  if (s === 'true') return true;
  if (s === 'false') return false;
  if (s === 'null') return null;
  if (/^-?\d+(\.\d+)?$/.test(s)) {
    const n = Number(s);
    if (Number.isFinite(n)) return n;
  }
  const trimmed = s.trim();
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    try { return JSON.parse(trimmed); } catch { /* keep as string */ }
  }
  return s;
}

// Auto-populate related fields based on data_index selection
const autoPopulateRelatedFields = async (apiClient: AxiosInstance, rows: any[], rowId: string, dataIndex: string, sourceField: 'data_index' | 'text', onDataChange: (newRows: any[]) => void) => {
  if (!dataIndex) return;

  try {
    console.log(`AUTO-POPULATE: Fetching related fields for data_index: ${dataIndex} (source: ${sourceField})`);
    const response = await apiClient.get('/dropdown/related-fields', {
      params: { data_index: dataIndex }
    });
    const data = response.data;

    if (data.success && data.fields) {
      const newRows = rows.map(row => {
        if (row.item_id === rowId) {
          console.log(`AUTO-POPULATE: Processing row ${rowId}, current label_text: "${row.label_text}"`);
          const updatedRow = { ...row };

          // Update related fields based on what was selected
          if (sourceField === 'data_index') {
            // User selected data_index, so update text/label fields and renderer
            // DON'T update data_index field itself to prevent reset
            if (data.fields.text) {
              console.log(`AUTO-POPULATE: Setting text to '${data.fields.text}' for row ${rowId}`);
              // Try to find the correct text field name in the row
              if ('text' in updatedRow) updatedRow.text = data.fields.text;
              else if ('label' in updatedRow) updatedRow.label = data.fields.text;
              else if ('label_text' in updatedRow) updatedRow.label_text = data.fields.text;
              else if ('display_text' in updatedRow) updatedRow.display_text = data.fields.text;
            }
            if (data.fields.renderer) {
              console.log(`AUTO-POPULATE: Setting renderer to '${data.fields.renderer}' for row ${rowId}`);
              updatedRow.renderer = data.fields.renderer;
            }
            // Populate formula fields if available
            if (data.fields.formula) {
              console.log(`AUTO-POPULATE: Setting formula to '${data.fields.formula}' for row ${rowId}`);
              if ('formula' in updatedRow) updatedRow.formula = data.fields.formula;
              if ('Formula' in updatedRow) updatedRow.Formula = data.fields.formula;
            }
            if (data.fields.calculation) {
              console.log(`AUTO-POPULATE: Setting calculation in extra_props for row ${rowId}`);
              const extraProps = updatedRow.extra_props ? JSON.parse(updatedRow.extra_props) : {};
              extraProps.calculation = data.fields.calculation;
              updatedRow.extra_props = JSON.stringify(extraProps);
            }
            // Preserve the original data_index value that was selected
            console.log(`AUTO-POPULATE: Preserving data_index value '${row.data_index}' for row ${rowId}`);
          } else if (sourceField === 'text') {
            // User selected text, so update data_index and renderer
            // DON'T update the text field itself - the user already chose the correct text value
            if (data.fields.dataIndex || data.fields.data_index) {
              const dataIndexValue = data.fields.dataIndex || data.fields.data_index;
              console.log(`AUTO-POPULATE: Setting data_index to '${dataIndexValue}' for row ${rowId}`);
              updatedRow.data_index = dataIndexValue;
            }
            if (data.fields.renderer) {
              console.log(`AUTO-POPULATE: Setting renderer to '${data.fields.renderer}' for row ${rowId}`);
              updatedRow.renderer = data.fields.renderer;
            }
            // Populate formula fields if available
            if (data.fields.formula) {
              console.log(`AUTO-POPULATE: Setting formula to '${data.fields.formula}' for row ${rowId}`);
              if ('formula' in updatedRow) updatedRow.formula = data.fields.formula;
              if ('Formula' in updatedRow) updatedRow.Formula = data.fields.formula;
            }
            if (data.fields.calculation) {
              console.log(`AUTO-POPULATE: Setting calculation in extra_props for row ${rowId}`);
              const extraProps = updatedRow.extra_props ? JSON.parse(updatedRow.extra_props) : {};
              extraProps.calculation = data.fields.calculation;
              updatedRow.extra_props = JSON.stringify(extraProps);
            }
            // Preserve the text field that was just selected by the user
            console.log(`AUTO-POPULATE: Preserving user-selected text value for row ${rowId}`);
            console.log(`AUTO-POPULATE: Current text field values - text: "${updatedRow.text}", label: "${updatedRow.label}", label_text: "${updatedRow.label_text}", display_text: "${updatedRow.display_text}"`);
          }

          return updatedRow;
        }
        return row;
      });

      onDataChange(newRows);
    } else {
      console.log(`AUTO-POPULATE: No related fields found for data_index: ${dataIndex}`);
    }
  } catch (error) {
    console.error('AUTO-POPULATE ERROR: Failed to get related fields:', error);
  }
};

/**  
 * A standalone editor for simple (primitive) values  
 * that only commits on Enter, without breaking hook rules.  
 */
function PrimitiveEditor({ name, value, onChange, readOnly = false }) {
  const [localValue, setLocalValue] = useState(String(value));
  useEffect(() => {
    setLocalValue(String(value));
  }, [value]);

  return (
    <TableRow>
      <TableCell component="th" sx={{ pr: 2 }}>{name}</TableCell>
      <TableCell>
        <TextField
          fullWidth
          variant="standard"
          value={localValue}
          onChange={readOnly ? undefined : e => setLocalValue(e.target.value)}
          onKeyDown={readOnly ? undefined : e => {
            if (e.key === 'Enter') {
              onChange(name, localValue);
            }
          }}
          InputProps={{
            readOnly: readOnly
          }}
        />
      </TableCell>
    </TableRow>
  );
}

/** 
 * Recursively render any JS value: 
 * - primitives as TextField 
 * - plain objects as key/value accordion 
 * - arrays of primitives as list rows 
 * - arrays of objects as per‑item accordions 
 */
function RenderValue({ name, value, onChange, readOnly = false }) {
  // console.log("RenderValue", name, value);
  // 1) primitives
  // if (value == null || typeof value !== 'object') {
  //   return (
  //     <TableRow>
  //       <TableCell component="th" sx={{ pr:2 }}>{name}</TableCell>
  //       <TableCell>
  //         <TextField
  //           fullWidth
  //           variant="standard"
  //           value={String(value)}
  //           onChange={e => onChange(name, e.target.value)}
  //         />
  //       </TableCell>
  //     </TableRow>
  //   );
  // }

  // 1) primitives (local state + commit on Enter)
  if (value == null || typeof value !== 'object') {
    return <PrimitiveEditor name={name} value={value} onChange={onChange} readOnly={readOnly} />;
  }

  // 2) array of primitives → render an editable TextField for each entry
  if (Array.isArray(value) && value.every(v => typeof v !== 'object')) {
    return (
      <TableRow>
        <TableCell component="th">{name}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {value.map((v, i) => (
              <TextField
                key={i}
                size="small"
                variant="standard"
                fullWidth
                label={`${name}[${i}]`}
                value={v}
                onChange={readOnly ? undefined : e => {
                  const copy = [...value];
                  copy[i] = e.target.value;
                  onChange(name, copy);
                }}
                InputProps={{
                  readOnly: readOnly
                }}
              />
            ))}
          </Box>
        </TableCell>
      </TableRow>
    );
  }

  // 3) array of objects → one accordion per item
  if (Array.isArray(value) && value.every(v => typeof v === 'object')) {
    return (
      <TableRow>
        <TableCell colSpan={2} sx={{ p: 0 }}>
          <Accordion sx={{ display: 'block', mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{name}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Table size="small">
                <TableBody>
                  {value.map((item, idx) => (
                    <React.Fragment key={idx}>
                      <TableRow>
                        <TableCell colSpan={2} sx={{ p: 0 }}>
                          <Accordion sx={{ display: 'block', mb: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography>{name} #{idx + 1}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Table size="small">
                                <TableBody>
                                  {Object.entries(item).map(([k, v]) => (
                                    <RenderValue
                                      key={k}
                                      name={k}
                                      value={v}
                                      onChange={(field, newVal) => {
                                        const copy = [...value];
                                        copy[idx] = { ...copy[idx], [field]: newVal };
                                        onChange(name, copy);
                                      }}
                                      readOnly={readOnly}
                                    />
                                  ))}
                                </TableBody>
                              </Table>
                            </AccordionDetails>
                          </Accordion>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        </TableCell>
      </TableRow>
    );
  }

  // 4) plain object → single accordion
  return (
    <TableRow>
      <TableCell colSpan={2} sx={{ p: 0 }}>
        <Accordion sx={{ display: 'block', mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{name}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Table size="small">
              <TableBody>
                {Object.entries(value).map(([k, v]) => (
                  <RenderValue
                    key={k}
                    name={k}
                    value={v}
                    onChange={(field, newVal) => {
                      const updated = { ...value, [field]: newVal };
                      onChange(name, updated);
                    }}
                    readOnly={readOnly}
                  />
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      </TableCell>
    </TableRow>
  );
}


/**
 * Format data_index based on source_type from catalog
 * For attribute, level, member: "{source_type}:{dataindex}:name"
 * For base_metric, computed_metric: keep as-is
 */
function formatDataIndex(dataIndex, catalog) {
  if (!dataIndex) return dataIndex;

  const meta = catalog[dataIndex] || {};
  const sourceType = meta.source_type;

  // Check if concatenation is needed
  if (sourceType === 'attribute' || sourceType === 'level' || sourceType === 'member') {
    return `${sourceType}:${dataIndex}:name`;
  }

  // For base_metric, computed_metric, or unknown types, keep as-is
  return dataIndex;
}

/**
 * Extract the original data_index from a potentially formatted value
 * e.g., "attribute:customer_id:name" -> "customer_id"
 */
function extractOriginalDataIndex(formattedValue) {
  if (!formattedValue) return formattedValue;

  // Check if it's a formatted value (has the pattern type:value:name)
  const parts = formattedValue.split(':');
  if (parts.length === 3 && parts[2] === 'name') {
    // It's formatted, return the middle part
    return parts[1];
  }

  // Not formatted, return as-is
  return formattedValue;
}

function hydrateFromCatalog(row, catalog) {
  const meta = catalog[row.data_index] || {};

  // Start with the current row (including its existing extra_props)
  const result = { ...row };

  // IMPORTANT: Preserve existing extra_props - don't overwrite them
  const existingExtraProps = row.extra_props || {};

  // Preserve manually entered label_text - don't overwrite if user has typed something
  const existingLabelText = row.label_text;

  // Copy fields from catalog EXCEPT extra_props, data_index, and label_text (if manually entered)
  Object.entries(meta).forEach(([k, v]) => {
    if (k === 'data_index' || k === 'extra_props') {
      // Skip data_index (already set) and extra_props (preserve existing)
      return;
    }

    if (k === 'label_text' && existingLabelText && existingLabelText.trim() !== '') {
      // Skip overwriting label_text if user has manually entered a value
      console.log(`DEBUG hydrateFromCatalog: Preserving existing label_text "${existingLabelText}" instead of catalog value "${v}"`);
      return;
    }

    // All other fields from catalog go directly to the row
    result[k] = v;
  });

  // Keep the existing extra_props (from the config)
  result.extra_props = existingExtraProps;

  return result;
}

/**
 * A single DynamicEditor that handles:
 * - a “props-only” accordion editor for rootProps
 * - a flat DataGrid for arrays of objects, with add/delete/move/add-child
 * - recurses into nested array/object for anything else
 */
export default function DynamicEditor({
  data,
  rootProps = {},
  onDataChange,
  onRootChange,
  onDirtyChange,
  tdOnlyMap,
  setTdOnlyMap,
  allowTdOnly = false,
  itemCatalog = {},
  itemCatalogKeys = [],
  isTopDown = false,
  recordMode = false,
  path = [],
  readOnly = false
}) {
  const apiClient = useApiClient();
  const apiRef = useGridApiRef();
  const lastKeyPressedRef = useRef(null);

  // ─── State ──────────────────────────────────────────
  const [expanded, setExpanded] = useState({});    // parentId → bool
  const [rowForProps, setRowForProps] = useState(null); // which row's extra_props drawer is open
  const [duplicateWarnings, setDuplicateWarnings] = useState({}); // item_id → warning message
  const [excelEditorOpen, setExcelEditorOpen] = useState(false); // Excel editor dialog state
  const [clickedCell, setClickedCell] = useState<{ rowId: string, field: string } | null>(null);
  const [sessionExtraPropKeys, setSessionExtraPropKeys] = useState<string[]>([]); // Session-based extra prop keys
  const [newExtraPropKey, setNewExtraPropKey] = useState(''); // For adding new keys
  const [selectedExtraPropKey, setSelectedExtraPropKey] = useState(''); // For selecting existing keys to add

  const rows = Array.isArray(data) ? data : [];

  // Dynamically collect all extra prop keys from existing rows
  const allExtraPropKeys = useMemo(() => {
    const keysSet = new Set<string>();
    rows.forEach(row => {
      if (row.extra_props && typeof row.extra_props === 'object') {
        Object.keys(row.extra_props).forEach(key => keysSet.add(key));
      }
    });
    return Array.from(keysSet).sort();
  }, [rows]);

  // Combine discovered keys with session keys
  const availableExtraPropKeys = useMemo(() => {
    const combined = new Set([...allExtraPropKeys, ...sessionExtraPropKeys]);
    return Array.from(combined).sort();
  }, [allExtraPropKeys, sessionExtraPropKeys]);

  // Memoize duplicate checking for performance
  const duplicateMap = useMemo(() => {
    const map = new Map();
    rows.forEach(row => {
      if (row.data_index) {
        if (!map.has(row.data_index)) {
          map.set(row.data_index, []);
        }
        map.get(row.data_index).push(row.item_id);
      }
    });
    return map;
  }, [rows.map(r => `${r.item_id}:${r.data_index}`).join('|')]);
  const isFlatRows = rows.length > 0 && typeof rows[0] === 'object';
  // console.log('🔍 DynamicEditor render:', { data, rows, isFlatRows, expanded });
  const propsOnlyMode = !isFlatRows && Object.keys(rootProps).length > 0;
  // Defensive: if keys weren’t passed, build them locally
  const keys = useMemo(
    () => itemCatalogKeys?.length ? itemCatalogKeys : Object.keys(itemCatalog || {}),
    [itemCatalogKeys, itemCatalog]
  );


  // ─── Utilities ─────────────────────────────────────
  // build a path key for TD-only checkboxes
  const getPathKey = key => [...path, key].join('.');

  // Build a map parentId→children 
  const childMap = useMemo(() => {
    const m = {};
    rows.forEach(r => {
      const pid = r.parent_item_id;
      if (pid) m[pid] = (m[pid] || []).concat(r);
    });
    return m;
  }, [rows]);

  // Build displayRows by walking roots and recursing only when expanded[id]===true
  const displayRows = useMemo(() => {
    const out = [];
    function walk(r, depth = 0) {
      out.push({ ...r, __depth: depth, __hasChildren: (childMap[r.item_id] || []).length > 0 });
      if (expanded[r.item_id]) {
        (childMap[r.item_id] || []).forEach(c => walk(c, depth + 1));
      }
    }
    rows.filter(r => !r.parent_item_id).forEach(r => walk(r));
    return out;
  }, [rows, childMap, expanded]);

  // ─── Handlers ──────────────────────────────────────

  // Handle Excel editor for rootprops
  const handleExcelSave = useCallback((newRootProps: Record<string, any>) => {
    onRootChange?.(newRootProps);
    onDirtyChange?.(data, tdOnlyMap, newRootProps);
  }, [data, tdOnlyMap, onRootChange, onDirtyChange]);


  // Called whenever any cell in the grid commits its change
  const handleCellEditCommit = useCallback(params => {
    console.log('✍️ handleCellEditCommit:', params);
    const { id, field, value } = params;
    const idx = rows.findIndex(r => r.item_id === id);
    if (idx < 0) return;
    // 1) clone & update the one row
    let updated = { ...rows[idx], [field]: value };
    // 2) if they changed data_index, hydrate the rest
    if (field === 'data_index') {
      updated = hydrateFromCatalog(updated, itemCatalog);
      delete updated._isNew;
    }
    // 3) notify parent
    const newData = [...rows];
    newData[idx] = updated;
    onDataChange(newData);
    onDirtyChange?.(newData, tdOnlyMap);
  }, [rows, onDataChange, onDirtyChange, itemCatalog, tdOnlyMap]);

  const processRowUpdate = useCallback((newRow, oldRow) => {
    console.log(`DEBUG processRowUpdate: Processing row ${newRow.item_id}`, {
      old_label_text: oldRow.label_text,
      new_label_text: newRow.label_text,
      old_data_index: oldRow.data_index,
      new_data_index: newRow.data_index,
      data_index_changed: newRow.data_index !== oldRow.data_index
    });

    // always apply your hydration, _isNew-clear, etc.
    let updated = { ...newRow };
    if (newRow.data_index !== oldRow.data_index) {
      console.log(`DEBUG processRowUpdate: Data index changed, hydrating from catalog for ${newRow.item_id}`);
      updated = hydrateFromCatalog(updated, itemCatalog);
      delete updated._isNew;
      console.log(`DEBUG processRowUpdate: After hydration, label_text is:`, updated.label_text);
    }

    // Push preview if user pressed Enter OR Tab (both should commit)
    if (lastKeyPressedRef.current === 'Enter' || lastKeyPressedRef.current === 'Tab') {
      console.log(`DEBUG processRowUpdate: Committing due to ${lastKeyPressedRef.current} key for ${updated.item_id}`);
      const newRows = rows.map(r =>
        r.item_id === updated.item_id ? updated : r
      );
      onDataChange(newRows);
      onDirtyChange?.(newRows, tdOnlyMap);
    }

    console.log(`DEBUG processRowUpdate: Final updated row:`, { id: updated.item_id, label_text: updated.label_text });
    return updated;
  }, [rows, onDataChange, onDirtyChange, tdOnlyMap, itemCatalog]);

  // When user clicks “Add Row”: flush any in-flight edit, then append
  const handleAdd = useCallback(() => {
    // tiny timeout lets DataGrid finish its own commit
    setTimeout(() => {
      const keys = Object.keys(rows[0] || {});
      const newRec = Object.fromEntries(keys.map(k => [k, '']));
      newRec.item_id = generateUUID();           // ← assign proper UUID
      newRec.parent_item_id = null;             // ← top-level row
      (newRec as any)._isNew = true; // ← MARK AS NEW

      // Initialize fields with proper types
      newRec.seq_no = '0';
      newRec.width = null;
      newRec.pinned = null;
      newRec.role = 'leaf';  // Default role

      // Copy extra_props from an existing row (they all should have the same config extra_props)
      let sourceExtraProps = {};
      if (rows.length > 0) {
        // Try to find a row that has extra_props
        for (const row of rows) {
          if (row.extra_props && typeof row.extra_props === 'object' && Object.keys(row.extra_props).length > 0) {
            sourceExtraProps = row.extra_props;
            break;
          }
        }
      }
      newRec.extra_props = JSON.stringify(sourceExtraProps);  // ← Copy extra_props as JSON string

      // Renumber seq_no so the new top-level row gets a unique slot at the
      // end (Bug 1).
      const newRows = renumberSeqNoFlat([...rows, newRec]);
      onDataChange(newRows);
      onDirtyChange?.(newRows, tdOnlyMap);
    }, 0);
  }, [rows, onDataChange, onDirtyChange, tdOnlyMap, setExpanded]);


  // Add a sibling row at the same nesting level as `referenceId`. Inserts
  // immediately AFTER the reference row in the array so it visually lands
  // right below it in the grid. Mirrors handleAddChild for edit-commit
  // flush, _isNew flagging, and extra_props copy — so the new row gets the
  // same amber unsaved-row highlight as a new top-level row.
  const handleAddSibling = useCallback((referenceId: string) => {
    // 1) Force commit any pending edits
    if (apiRef.current) {
      const commonFields = ['item_id', 'label_text', 'data_index', 'renderer', 'width', 'pinned', 'seq_no', 'role'];
      rows.forEach(row => {
        commonFields.forEach(field => {
          try {
            if (apiRef.current.getCellMode && apiRef.current.getCellMode(row.item_id, field) === 'edit') {
              apiRef.current.stopCellEditMode({ id: row.item_id, field });
            }
          } catch (e) { /* not editable, skip */ }
        });
      });
    }

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    setTimeout(() => {
      let currentRows = rows;
      if (apiRef.current && apiRef.current.getRowModels) {
        try {
          currentRows = Array.from(apiRef.current.getRowModels().values());
        } catch (e) { /* fall back to props rows */ }
      }

      const referenceRow = currentRows.find(r => r.item_id === referenceId);
      if (!referenceRow) {
        console.warn(`handleAddSibling: reference row ${referenceId} not found`);
        return;
      }

      const keys = Object.keys(currentRows[0] || {});
      const sibling: any = Object.fromEntries(keys.map(k => [k, '']));
      sibling.item_id = generateUUID();
      sibling.parent_item_id = referenceRow.parent_item_id ?? null;
      sibling._isNew = true;
      sibling.seq_no = '0';
      sibling.width = null;
      sibling.pinned = null;
      sibling.role = referenceRow.role || 'leaf';

      // Copy extra_props from reference; fall back to any existing row.
      let sourceExtraProps: any = {};
      if (referenceRow.extra_props && typeof referenceRow.extra_props === 'object' && Object.keys(referenceRow.extra_props).length > 0) {
        sourceExtraProps = referenceRow.extra_props;
      } else {
        for (const r of currentRows) {
          if (r.extra_props && typeof r.extra_props === 'object' && Object.keys(r.extra_props).length > 0) {
            sourceExtraProps = r.extra_props;
            break;
          }
        }
      }
      sibling.extra_props = JSON.stringify(sourceExtraProps);

      // Insert immediately after the reference row.
      const refIdx = currentRows.findIndex(r => r.item_id === referenceId);
      const inserted = [
        ...currentRows.slice(0, refIdx + 1),
        sibling,
        ...currentRows.slice(refIdx + 1)
      ];
      // Renumber seq_no so the new row's array position is what gets
      // persisted (Bug 1: with the placeholder seq_no='0' it would sort
      // to the wrong slot on reload, even though the in-session display
      // looked correct).
      const newRows = renumberSeqNoFlat(inserted);
      onDataChange(newRows);
      onDirtyChange?.(newRows, tdOnlyMap);
    }, 150);
  }, [rows, onDataChange, onDirtyChange, tdOnlyMap, apiRef]);

  const handleAddChild = useCallback(parentId => {
    console.log(`DEBUG handleAddChild: Adding child to parent ${parentId}`);

    // 1) Force commit any pending edits by calling the API directly
    if (apiRef.current) {
      // Try to stop edit mode for all visible rows and common fields
      const commonFields = ['item_id', 'label_text', 'data_index', 'renderer', 'width', 'pinned', 'seq_no', 'role'];
      let editingCellsCount = 0;

      rows.forEach(row => {
        commonFields.forEach(field => {
          try {
            // Check if cell is in edit mode and stop it
            if (apiRef.current.getCellMode && apiRef.current.getCellMode(row.item_id, field) === 'edit') {
              apiRef.current.stopCellEditMode({ id: row.item_id, field });
              editingCellsCount++;
            }
          } catch (e) {
            // Silently continue if cell is not editable
          }
        });
      });

      console.log(`DEBUG handleAddChild: Stopped ${editingCellsCount} editing cells`);
    }

    // 2) Also blur the active element
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // 3) Wait for commits to propagate
    setTimeout(() => {
      // Get the current row data directly from DataGrid API if available
      let currentRows = rows;
      if (apiRef.current && apiRef.current.getRowModels) {
        try {
          const rowModels = apiRef.current.getRowModels();
          currentRows = Array.from(rowModels.values());
          console.log(`DEBUG handleAddChild: Got current rows from DataGrid API:`, currentRows.map(r => ({ id: r.item_id, label_text: r.label_text })));
        } catch (e) {
          console.log(`DEBUG handleAddChild: Could not get rows from API, using props:`, e);
        }
      } else {
        console.log(`DEBUG handleAddChild: Using rows from props:`, currentRows.map(r => ({ id: r.item_id, label_text: r.label_text })));
      }

      const keys = Object.keys(currentRows[0] || {});
      const child = Object.fromEntries(keys.map(k => [k, '']));
      child.parent_item_id = parentId;
      (child as any)._isNew = true;
      child.item_id = generateUUID();

      // Initialize fields with proper types
      child.seq_no = '0';
      child.width = null;
      child.pinned = null;
      child.role = 'leaf';  // Default role

      // Copy extra_props from parent row or any existing row
      let sourceExtraProps = {};
      const parentRow = currentRows.find(r => r.item_id === parentId);

      console.log(`DEBUG handleAddChild: Found parent row:`, parentRow ? { id: parentRow.item_id, label_text: parentRow.label_text } : 'NOT FOUND');

      // Try parent first
      if (parentRow && parentRow.extra_props && typeof parentRow.extra_props === 'object' && Object.keys(parentRow.extra_props).length > 0) {
        sourceExtraProps = parentRow.extra_props;
      } else {
        // Fallback to finding any row with extra_props
        for (const row of currentRows) {
          if (row.extra_props && typeof row.extra_props === 'object' && Object.keys(row.extra_props).length > 0) {
            sourceExtraProps = row.extra_props;
            break;
          }
        }
      }
      child.extra_props = JSON.stringify(sourceExtraProps);  // ← Copy extra_props as JSON string

      const inserted = [...currentRows, child];
      // Renumber seq_no so the new child sorts after existing siblings
      // (Bug 1). Without this, the placeholder seq_no='0' ties with any
      // existing first sibling and the row pops to the top on reload.
      const newRows = renumberSeqNoFlat(inserted);
      console.log(`DEBUG handleAddChild: Final rows after adding child:`, newRows.map(r => ({ id: r.item_id, label_text: r.label_text, parent: r.parent_item_id })));

      onDataChange(newRows);
      onDirtyChange?.(newRows, tdOnlyMap);

      setExpanded(exp => ({ ...exp, [parentId]: true }));
    }, 150); // Longer timeout to ensure commits have fully propagated
  }, [rows, onDataChange, onDirtyChange, tdOnlyMap, setExpanded, apiRef]);


  // Delete a row at index
  const handleDelete = useCallback(idx => {
    const deletedRow = rows[idx];
    const newRows = rows.filter((_, i) => i !== idx);

    // Clear any warning for the deleted row and recalculate warnings for remaining rows
    if (deletedRow && deletedRow.item_id) {
      setDuplicateWarnings(prev => {
        const updated = { ...prev };
        delete updated[deletedRow.item_id];

        // Recalculate warnings for remaining rows since a duplicate might have been removed
        Object.keys(updated).forEach(itemId => {
          const row = newRows.find(r => r.item_id === itemId);
          if (row && row.data_index) {
            const stillHasDuplicate = newRows.some(r =>
              r.item_id !== itemId && r.data_index === row.data_index
            );
            if (!stillHasDuplicate) {
              delete updated[itemId];
            }
          }
        });

        return updated;
      });
    }

    onDataChange(newRows);
    onDirtyChange?.(newRows, tdOnlyMap);
  }, [rows, onDataChange, onDirtyChange, tdOnlyMap, setDuplicateWarnings]);

  // Move up/down — renumberSeqNoFlat (hoisted, see top of file) is the shared
  // helper. Called from both Move and Add paths so seq_no always reflects
  // array order before save.
  const handleMoveUp = useCallback(idx => {
    if (idx <= 0) return;
    const arr = [...rows];
    const [item] = arr.splice(idx, 1);
    arr.splice(idx - 1, 0, item);
    const renumbered = renumberSeqNoFlat(arr);
    onDataChange(renumbered);
    onDirtyChange?.(renumbered, tdOnlyMap);
  }, [rows, onDataChange, onDirtyChange, tdOnlyMap]);
  const handleMoveDown = useCallback(idx => {
    if (idx >= rows.length - 1) return;
    const arr = [...rows];
    const [item] = arr.splice(idx, 1);
    arr.splice(idx + 1, 0, item);
    const renumbered = renumberSeqNoFlat(arr);
    onDataChange(renumbered);
    onDirtyChange?.(renumbered, tdOnlyMap);
  }, [rows, onDataChange, onDirtyChange, tdOnlyMap]);

  // TD-Only checkbox toggles
  const handleTdOnlyChange = useCallback((id, checked) => {
    const key = getPathKey(id);
    setTdOnlyMap(m => ({ ...m, [key]: checked }));
    onDirtyChange?.(rows, { ...tdOnlyMap, [key]: checked });
  }, [path, rows, tdOnlyMap, onDirtyChange]);

  // Extra-props drawer handler
  const handleExtraPropChange = useCallback((row, newProps) => {
    const idx = rows.findIndex(r => r.item_id === row.item_id);
    if (idx < 0) return;
    const arr = [...rows];
    arr[idx] = { ...arr[idx], extra_props: newProps };
    onDataChange(arr);
    setRowForProps(r => r && r.item_id === row.item_id
      ? { ...r, extra_props: newProps }
      : r
    );
  }, [rows, onDataChange]);

  // Add new extra prop key to session
  const handleAddNewExtraPropKey = useCallback(() => {
    if (newExtraPropKey.trim() && !availableExtraPropKeys.includes(newExtraPropKey.trim())) {
      setSessionExtraPropKeys(prev => [...prev, newExtraPropKey.trim()]);
      setNewExtraPropKey('');
    }
  }, [newExtraPropKey, availableExtraPropKeys]);

  // Add selected extra prop key to current row
  const handleAddExtraPropToRow = useCallback(() => {
    if (selectedExtraPropKey && rowForProps) {
      // Parse extra_props if it's a string
      const currentProps = typeof rowForProps.extra_props === 'string'
        ? JSON.parse(rowForProps.extra_props || '{}')
        : (rowForProps.extra_props || {});
      if (!(selectedExtraPropKey in currentProps)) {
        const updatedProps = { ...currentProps, [selectedExtraPropKey]: '' };
        handleExtraPropChange(rowForProps, updatedProps);
        setSelectedExtraPropKey('');
      }
    }
  }, [selectedExtraPropKey, rowForProps, handleExtraPropChange]);

  // Delete extra prop key from current row
  const handleDeleteExtraProp = useCallback((keyToDelete) => {
    if (rowForProps) {
      const currentProps = { ...(rowForProps.extra_props || {}) };
      delete currentProps[keyToDelete];
      handleExtraPropChange(rowForProps, currentProps);
    }
  }, [rowForProps, handleExtraPropChange]);

  // ─── Columns ──────────────────────────────────────

  // Expander column: shows chevron only when __hasChildren
  const expanderCol = useMemo(() => ({
    field: '__expander',
    headerName: '',
    width: 80,
    cellClassName: 'expander-cell',
    sortable: false, filterable: false, disableColumnMenu: true,
    renderCell: params => {
      const { row } = params;
      if (!row.__hasChildren) return null;
      const isOpen = !!expanded[row.item_id];
      return (
        <Box sx={{ ml: row.__depth * 2, overflow: 'visible' }}>
          <IconButton
            size="small"
            onClick={() => {
              setExpanded(e => ({ ...e, [row.item_id]: !isOpen }));
            }}
          >
            {isOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Box>
      );
    }
  }), [expanded]);

  // TD-Only column
  const tdOnlyCol = useMemo(() => ({
    field: '__td',
    headerName: 'TD Only',
    width: 90,
    cellClassName: 'td-only-cell',
    sortable: false, filterable: false, disableColumnMenu: true,
    disableClickEventBubbling: true,
    renderCell: params => (
      <Checkbox
        size="small"
        checked={!!tdOnlyMap[getPathKey(params.id)]}
        onChange={e => handleTdOnlyChange(params.id, e.target.checked)}
      />
    )
  }), [tdOnlyMap, path, rows, onDirtyChange]);

  // Action column: move up/down, delete, add-child, extra-props
  const actionCol = useMemo(() => ({
    field: '__actions',
    headerName: 'Actions',
    width: 280,
    cellClassName: 'action-cell',
    sortable: false, filterable: false, disableColumnMenu: true,
    disableClickEventBubbling: true,
    renderCell: params => {
      const idx = rows.findIndex(r => r.item_id === params.row.item_id);
      return (
        <>
          <Tooltip title="Move Up"><span><IconButton size="small" onClick={() => handleMoveUp(idx)} disabled={idx === 0}><ArrowUpwardIcon /></IconButton></span></Tooltip>
          <Tooltip title="Move Down"><span><IconButton size="small" onClick={() => handleMoveDown(idx)} disabled={idx === rows.length - 1}><ArrowDownwardIcon /></IconButton></span></Tooltip>
          <Tooltip title="Delete Row"><span><IconButton size="small" onClick={() => handleDelete(idx)}><DeleteIcon /></IconButton></span></Tooltip>
          <Tooltip title="Add Sibling"><IconButton size="small" onClick={() => handleAddSibling(params.row.item_id)}><PlaylistAddIcon /></IconButton></Tooltip>
          <Tooltip title="Add Child"><IconButton size="small" onClick={() => handleAddChild(params.row.item_id)}><AddIcon /></IconButton></Tooltip>
          <Tooltip title="Edit Extra Props"><IconButton size="small" onClick={() => setRowForProps(params.row)}><Settings /></IconButton></Tooltip>
        </>
      );
    }
  }), [rows, handleMoveUp, handleMoveDown, handleDelete, handleAddSibling, handleAddChild]);

  // Memoize base schema to avoid recalculation
  const baseSchema = useMemo(() => {
    if (!isFlatRows || rows.length === 0) return [];
    return Object.keys(rows[0])
      .filter(k => ![
        'item_id', 'parent_item_id', 'extra_props',
        'created_at', 'created_by', 'updated_at', 'updated_by', 'view_name', 'seq_no', 'role'
      ].includes(k));
  }, [isFlatRows, rows.length > 0 ? Object.keys(rows[0]).join('') : '']);

  // Flat columns: one per data field, plus Autocomplete for data_index
  const flatColumns = useMemo(() => {
    if (!isFlatRows) return [];
    return baseSchema.map(field => {
      const header = field.replace(/_/g, ' ').toUpperCase();
      // 1) build the shared column settings
      const common = {
        field,
        headerName: header,
        minWidth: 120,
        flex: 1,
        resizable: true,
        editable: true,
        sortable: true,
        hideable: true
      };
      // Enhanced data_index column with database-driven suggestions
      if (field === 'data_index') {
        return {
          ...common,
          minWidth: 250,                    // Wider default for data_index column
          flex: 2,                          // Give it more space
          editable: true,                 // never let the grid switch to its own editor
          cellClassName: 'edit-dropdown-cell',
          renderCell: ({ row }) => {
            return (
              <Box sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                ...(duplicateWarnings[row.item_id] && {
                  borderLeft: '3px solid',
                  borderLeftColor: 'warning.main',
                  pl: 1
                })
              }}>
                <span>{row.data_index || ''}</span>
                {duplicateWarnings[row.item_id] && (
                  <span style={{ color: '#f57c00', marginLeft: 8, fontSize: '12px' }}>⚠</span>
                )}
              </Box>
            );
          },
          renderEditCell: (params) => {
            const { row, api, id } = params;
            return (
              <AutocompleteDropdown
                value={row.data_index || ''}
                onChange={(value) => {
                  // Handle when value is cleared/deleted
                  if (!value) {
                    // Clear warning
                    setDuplicateWarnings(prev => {
                      const updated = { ...prev };
                      delete updated[row.item_id];
                      return updated;
                    });

                    // Update row with empty data_index
                    let updated = { ...row, data_index: '' };

                    // Clear other fields that were populated from catalog
                    Object.keys(row).forEach(key => {
                      if (key !== 'item_id' && key !== 'parent_item_id' && key !== 'extra_props' && key !== '_isNew') {
                        updated[key] = '';
                      }
                    });

                    const newRows = rows.map(r =>
                      r.item_id === row.item_id ? updated : r
                    );
                    onDataChange(newRows);
                    onDirtyChange?.(newRows, tdOnlyMap);
                    api.setEditCellValue({ id, field: 'data_index', value });
                    return;
                  }

                  // For regular typing (not selection), update immediately
                  // Check for duplicates using memoized map
                  const duplicateRows = duplicateMap.get(value) || [];
                  const isDuplicate = duplicateRows.length > 0 && !duplicateRows.every(id => id === row.item_id);

                  // Update or clear warning
                  setDuplicateWarnings(prev => {
                    const updated = { ...prev };
                    if (isDuplicate && value) {
                      updated[row.item_id] = `Warning: "${value}" already exists in another row`;
                    } else {
                      delete updated[row.item_id];
                    }
                    return updated;
                  });
                  api.setEditCellValue({ id, field: 'data_index', value });
                }}
                onSelect={(value, metadata) => {
                  // Handle both data_index update and auto-population in one operation
                  console.log(`data_index onSelect: value="${value}", metadata:`, metadata);

                  // Check for duplicates with the selected value using memoized map
                  const duplicateRows = duplicateMap.get(value) || [];
                  const isDuplicate = duplicateRows.length > 0 && !duplicateRows.every(id => id === row.item_id);

                  // Update or clear warning
                  setDuplicateWarnings(prev => {
                    const updated = { ...prev };
                    if (isDuplicate && value) {
                      updated[row.item_id] = `Warning: "${value}" already exists in another row`;
                    } else {
                      delete updated[row.item_id];
                    }
                    return updated;
                  });

                  // Update the row with new data_index
                  let updated = { ...row, data_index: value };

                  // Remove _isNew flag if it exists
                  if (updated._isNew) {
                    delete updated._isNew;
                  }

                  // Ensure all fields have at least empty string values for editability
                  Object.keys(row).forEach(key => {
                    if (updated[key] === undefined || updated[key] === null) {
                      updated[key] = '';
                    }
                  });

                  const newRows = rows.map(r =>
                    r.item_id === row.item_id ? updated : r
                  );
                  onDataChange(newRows);
                  onDirtyChange?.(newRows, tdOnlyMap);

                  // Auto-populate related fields when a database suggestion is selected
                  if (metadata?.id) {
                    autoPopulateRelatedFields(apiClient, newRows, row.item_id, metadata.id, 'data_index', onDataChange);
                  }

                  api.setEditCellValue({ id, field: 'data_index', value });
                  api.stopCellEditMode({ id, field: 'data_index' });
                }}
                placeholder="Search data index..."
                contextKey="data_index"
                entityTypes={getEntityTypesForColumn('data_index')}
              />
            );
          }
        };
      }

      // Enhanced text field with database-driven suggestions (handles 'text', 'label', 'label_text', etc.)
      if (field === 'text' || field === 'label' || field === 'label_text' || field === 'display_text') {
        return {
          ...common,
          minWidth: 200,
          flex: 1.5,
          editable: true,
          cellClassName: 'edit-dropdown-cell',
          renderCell: ({ row }) => {
            return (
              <Box sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center'
              }}>
                <span>{row[field] || ''}</span>
              </Box>
            );
          },
          renderEditCell: (params) => {
            const { row, api, id } = params;
            return (
              <AutocompleteDropdown
                value={row[field] || ''}
                onChange={(value) => {
                  console.log(`DEBUG DynamicEditor onChange: field="${field}", value="${value}"`);
                  const updated = { ...row, [field]: value };
                  const newRows = rows.map(r =>
                    r.item_id === row.item_id ? updated : r
                  );
                  console.log(`DEBUG DynamicEditor: Updated row [field]="${updated[field]}"`);
                  onDataChange(newRows);
                  onDirtyChange?.(newRows, tdOnlyMap);
                  api.setEditCellValue({ id, field, value });
                }}
                onSelect={(value, metadata) => {
                  console.log(`DEBUG DynamicEditor onSelect: field="${field}", value="${value}", hasMetadata=${!!metadata?.id}`);
                  // Always update the field value first
                  api.setEditCellValue({ id, field, value });

                  // Auto-populate related fields only when a database suggestion is selected (not manual entry)
                  if (metadata?.id) {
                    const updatedRows = rows.map(r =>
                      r.item_id === row.item_id ? { ...r, [field]: value } : r
                    );
                    autoPopulateRelatedFields(apiClient, updatedRows, row.item_id, metadata.id, 'text', onDataChange);
                  } else {
                    // For manual entry, just update the current field without auto-population
                    const updated = { ...row, [field]: value };
                    const newRows = rows.map(r =>
                      r.item_id === row.item_id ? updated : r
                    );
                    onDataChange(newRows);
                    onDirtyChange?.(newRows, tdOnlyMap);
                  }
                  api.stopCellEditMode({ id, field });
                }}
                placeholder={`Search ${field.replace(/_/g, ' ')}...`}
                contextKey="text"
                entityTypes={getEntityTypesForColumn(field)}
              />
            );
          }
        };
      }

      // Enhanced renderer field with database-driven suggestions  
      if (field === 'renderer') {
        return {
          ...common,
          minWidth: 150,
          editable: true,
          cellClassName: 'edit-dropdown-cell',
          renderCell: ({ row }) => {
            return (
              <Box sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center'
              }}>
                <span>{row.renderer || ''}</span>
              </Box>
            );
          },
          renderEditCell: (params) => {
            const { row, api, id } = params;

            // Common renderer options
            const commonRenderers = [
              'text',
              'number',
              'date',
              'currency',
              'percentage',
              'boolean',
              'link',
              'image',
              'button',
              'icon',
              'custom',
              'template',
              'html',
              'markdown',
              'badge',
              'chip',
              'progress',
              'rating',
              'color',
              'json'
            ];

            return (
              <AutocompleteDropdown
                value={row.renderer || ''}
                onChange={(value) => {
                  const updated = { ...row, renderer: value };
                  const newRows = rows.map(r =>
                    r.item_id === row.item_id ? updated : r
                  );
                  onDataChange(newRows);
                  onDirtyChange?.(newRows, tdOnlyMap);
                  api.setEditCellValue({ id, field: 'renderer', value });
                }}
                onSelect={(value, metadata) => {
                  api.setEditCellValue({ id, field: 'renderer', value });
                  api.stopCellEditMode({ id, field: 'renderer' });
                }}
                placeholder="Search or select renderer..."
                contextKey="renderer"
                entityTypes={getEntityTypesForColumn('renderer')}
                staticOptions={commonRenderers}
              />
            );
          }
        };
      }

      // Enhanced editor field with database-driven suggestions
      if (field === 'editor') {
        return {
          ...common,
          minWidth: 150,
          editable: true,
          cellClassName: 'edit-dropdown-cell',
          renderCell: ({ row }) => {
            return (
              <Box sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center'
              }}>
                <span>{row.editor || ''}</span>
              </Box>
            );
          },
          renderEditCell: (params) => {
            const { row, api, id } = params;
            return (
              <AutocompleteDropdown
                value={row.editor || ''}
                onChange={(value) => {
                  const updated = { ...row, editor: value };
                  const newRows = rows.map(r =>
                    r.item_id === row.item_id ? updated : r
                  );
                  onDataChange(newRows);
                  onDirtyChange?.(newRows, tdOnlyMap);
                  api.setEditCellValue({ id, field: 'editor', value });
                }}
                onSelect={(value, metadata) => {
                  api.setEditCellValue({ id, field: 'editor', value });
                  api.stopCellEditMode({ id, field: 'editor' });
                }}
                placeholder="Search editor..."
                contextKey="editor"
                entityTypes={getEntityTypesForColumn('editor')}
              />
            );
          }
        };
      }

      // 3) otherwise, just return the common definition
      return common;
    });
  }, [baseSchema, duplicateMap, clickedCell?.rowId, clickedCell?.field]);

  //   console.table(
  //   '💠 flatColumns',
  //   flatColumns.map(c => ({
  //     field: c.field,
  //     hasRenderEdit: !!c.renderEditCell,
  //     hasRenderCell: !!c.renderCell,
  //     editable: c.editable,
  //     cellClass: c.cellClassName
  //   }))
  // );

  // Helper function to check which columns have any non-empty values
  const hasColumnValues = useMemo(() => {
    const fieldsWithValues = new Set<string>();

    rows.forEach(row => {
      baseSchema.forEach(field => {
        const value = row[field];
        // Consider a field as having values if it has non-empty, non-null, non-undefined values
        if (value !== null && value !== undefined && value !== '' && value !== 0) {
          fieldsWithValues.add(field);
        }
      });
    });

    return fieldsWithValues;
  }, [rows, baseSchema]);

  // Filter flatColumns to show only columns with values
  const visibleFlatColumns = useMemo(() => {
    return flatColumns.filter(column => hasColumnValues.has(column.field));
  }, [flatColumns, hasColumnValues]);

  const columnsWithExpander = useMemo(() => {
    const cols = [expanderCol];
    if (allowTdOnly) cols.push(tdOnlyCol);
    return cols.concat(visibleFlatColumns as any, actionCol as any);
  }, [expanderCol, tdOnlyCol, visibleFlatColumns, actionCol, allowTdOnly]);

  //   console.log(
  //   '💠 columnsWithExpander',
  //   columnsWithExpander.map(c => c.field)
  // );


  // ─── RENDER ─────────────────────────────────────────
  // Props-only mode
  if (propsOnlyMode) {
    return (
      <Box sx={{ mb: 2, width: '100%' }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Root Properties</Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setExcelEditorOpen(true)}
          >
            Edit
          </Button>
        </Box>

        <Table size="small">
          <TableBody>
            {Object.entries(rootProps).map(([k, v]) => (
              <RenderValue
                key={k}
                name={k}
                value={v}
                onChange={() => { }} // Read-only - no onChange functionality
                readOnly={true}
              />
            ))}
          </TableBody>
        </Table>

        <RootPropsExcelEditor
          open={excelEditorOpen}
          onClose={() => setExcelEditorOpen(false)}
          rootProps={rootProps}
          onSave={handleExcelSave}
        />
      </Box>
    );
  }

  // Flat/grid mode
  if (isFlatRows) {
    return (
      <>
        {Object.keys(rootProps).length > 0 && (
          <Box sx={{ mb: 2, width: '100%' }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Root Properties</Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setExcelEditorOpen(true)}
              >
                Edit
              </Button>
            </Box>

            <Table size="small">
              <TableBody>
                {Object.entries(rootProps).map(([propName, propVal]) => (
                  <RenderValue
                    key={propName}
                    name={propName}
                    value={propVal}
                    onChange={() => { }} // Read-only - no onChange functionality
                    readOnly={true}
                  />
                ))}
              </TableBody>
            </Table>

            <RootPropsExcelEditor
              open={excelEditorOpen}
              onClose={() => setExcelEditorOpen(false)}
              rootProps={rootProps}
              onSave={handleExcelSave}
            />
          </Box>
        )}
        <Box sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setTimeout(handleAdd, 0)}  // flush any in-flight edits
            >
              Add Row
            </Button>
          </Box>

          <DataGrid
            rows={displayRows}
            columns={columnsWithExpander}
            apiRef={apiRef}
            getRowId={r => r.item_id}
            getRowClassName={(params) => (params.row as any)._isNew ? 'unsaved-new-row' : ''}
            editMode="cell"
            disableRowSelectionOnClick
            disableColumnMenu
            // Single-click to enter edit mode for editable cells. Default
            // DataGrid behavior is select-on-click + double-click-to-edit,
            // which is painful when typing into many cells in a new row.
            onCellClick={(params, event) => {
              const colDef = params.colDef as any;
              const isNonEditable =
                params.field === '__expander' ||
                params.field === '__actions' ||
                params.field === '__td' ||
                colDef?.editable === false;
              if (isNonEditable) return;
              try {
                if (apiRef.current?.getCellMode(params.id, params.field) !== 'edit') {
                  apiRef.current?.startCellEditMode({ id: params.id, field: params.field });
                }
              } catch { /* cell not editable, ignore */ }
            }}
            onCellKeyDown={(params, event) => {
              lastKeyPressedRef.current = event.key;
              // Enable Tab navigation with commit
              if (event.key === 'Tab' && !event.shiftKey) {
                event.preventDefault();

                // Check if current cell is in edit mode before trying to stop it
                const cellMode = apiRef.current.getCellMode(params.id, params.field);
                if (cellMode === 'edit') {
                  // Stop edit mode on current cell (this commits the value)
                  apiRef.current.stopCellEditMode({
                    id: params.id,
                    field: params.field
                  });
                }

                // Then move to next editable cell
                // Get only truly editable columns (exclude expander, actions, td-only, etc.)
                const editableColumns = columnsWithExpander.filter(col =>
                  col.field !== '__expander' &&
                  col.field !== '__actions' &&
                  col.field !== '__td' &&
                  col.field !== 'data_index' && // Skip data_index as it has custom editor
                  (col as any).editable !== false
                );

                const currentFieldIndex = columnsWithExpander.findIndex(col => col.field === params.field);
                // Find next editable column after current position
                let nextEditableCol = null;
                for (let i = currentFieldIndex + 1; i < columnsWithExpander.length; i++) {
                  const col = columnsWithExpander[i];
                  if (editableColumns.some(ec => ec.field === col.field)) {
                    nextEditableCol = col;
                    break;
                  }
                }

                if (nextEditableCol) {
                  setTimeout(() => {
                    apiRef.current.setCellFocus(params.id, nextEditableCol.field);
                    apiRef.current.startCellEditMode({ id: params.id, field: nextEditableCol.field });
                  }, 50); // Small delay to ensure commit completes
                } else {
                  // At end of row, move to first editable cell of next row
                  const currentRowIndex = displayRows.findIndex(r => r.item_id === params.id);
                  if (currentRowIndex < displayRows.length - 1) {
                    const nextRowId = displayRows[currentRowIndex + 1].item_id;
                    const firstEditableField = editableColumns[0]?.field;
                    if (firstEditableField) {
                      setTimeout(() => {
                        apiRef.current.setCellFocus(nextRowId, firstEditableField);
                        apiRef.current.startCellEditMode({ id: nextRowId, field: firstEditableField });
                      }, 50);
                    }
                  }
                }
              }

              // Handle Shift+Tab for backward navigation
              if (event.key === 'Tab' && event.shiftKey) {
                event.preventDefault();

                // Check if current cell is in edit mode before trying to stop it
                const cellMode = apiRef.current.getCellMode(params.id, params.field);
                if (cellMode === 'edit') {
                  // Stop edit mode on current cell (commits the value)
                  apiRef.current.stopCellEditMode({
                    id: params.id,
                    field: params.field
                  });
                }

                // Move to previous editable cell
                // Get only truly editable columns (same filter as forward tab)
                const editableColumns = columnsWithExpander.filter(col =>
                  col.field !== '__expander' &&
                  col.field !== '__actions' &&
                  col.field !== '__td' &&
                  col.field !== 'data_index' && // Skip data_index as it has custom editor
                  (col as any).editable !== false
                );

                const currentFieldIndex = columnsWithExpander.findIndex(col => col.field === params.field);
                // Find previous editable column before current position
                let prevEditableCol = null;
                for (let i = currentFieldIndex - 1; i >= 0; i--) {
                  const col = columnsWithExpander[i];
                  if (editableColumns.some(ec => ec.field === col.field)) {
                    prevEditableCol = col;
                    break;
                  }
                }

                if (prevEditableCol) {
                  setTimeout(() => {
                    apiRef.current.setCellFocus(params.id, prevEditableCol.field);
                    apiRef.current.startCellEditMode({ id: params.id, field: prevEditableCol.field });
                  }, 50);
                } else {
                  // At beginning of row, move to last editable cell of previous row
                  const currentRowIndex = displayRows.findIndex(r => r.item_id === params.id);
                  if (currentRowIndex > 0) {
                    const prevRowId = displayRows[currentRowIndex - 1].item_id;
                    const lastEditableField = editableColumns[editableColumns.length - 1]?.field;
                    if (lastEditableField) {
                      setTimeout(() => {
                        apiRef.current.setCellFocus(prevRowId, lastEditableField);
                        apiRef.current.startCellEditMode({ id: prevRowId, field: lastEditableField });
                      }, 50);
                    }
                  }
                }
              }
            }}
            // onCellEditCommit={handleCellEditCommit}
            // Prevent blur from committing: re-enter edit-mode on focus-out
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={err => console.error(err)}
            disableColumnResize={false}
            // experimentalFeatures={{ columnResizing: true }}
            // columnResizeMode="fit"
            sx={{
              height: '70vh',                // Fixed height with scroll
              // restore grid lines for cells
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid rgba(224,224,224,1)'
              },
              // Sticky headers with distinct styling
              '& .MuiDataGrid-columnHeaders': {
                position: 'sticky',
                top: 0,
                backgroundColor: 'background.paper',
                borderBottom: '2px solid rgba(224,224,224,1)',
                zIndex: 1
              },
              // Row hover effect
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover'
              },
              // Alternating row colors
              '& .MuiDataGrid-row:nth-of-type(even)': {
                backgroundColor: 'grey.50'
              },
              // Unsaved (newly added) rows — soft amber, beats alt-row + hover
              '& .MuiDataGrid-row.unsaved-new-row': {
                backgroundColor: '#FFF8E1',
                borderLeft: '3px solid #FFB300'
              },
              '& .MuiDataGrid-row.unsaved-new-row:hover': {
                backgroundColor: '#FFECB3'
              },
              // allow chevrons & action icons to overflow their cells
              '& .MuiDataGrid-cell.expander-cell': { overflow: 'visible !important' },
              '& .MuiDataGrid-cell.action-cell': { overflow: 'visible !important' },
              '& .edit-dropdown-cell': { overflow: 'visible !important' },
            }}
          />

          <Button
            variant="contained"
            size="small"
            sx={{ mt: 1 }}
            onClick={() => setTimeout(handleAdd, 0)}  // Same handler as top button
          >
            Add Row
          </Button>

          <Drawer
            anchor="right"
            open={!!rowForProps}
            onClose={() => setRowForProps(null)}
            sx={{ zIndex: 1400 }}
          >
            <Box sx={{ width: 500, p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Extra Props</Typography>

              {/* Session Key Management */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Manage Extra Prop Keys</Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    placeholder="New key name"
                    value={newExtraPropKey}
                    onChange={(e) => setNewExtraPropKey(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddNewExtraPropKey()}
                  />
                  <Button size="small" onClick={handleAddNewExtraPropKey} disabled={!newExtraPropKey.trim()}>
                    Add Key
                  </Button>
                </Stack>

                <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>Available Keys:</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {availableExtraPropKeys.map((key) => (
                    <Chip
                      key={key}
                      label={key}
                      size="small"
                      variant={sessionExtraPropKeys.includes(key) ? "filled" : "outlined"}
                    />
                  ))}
                </Stack>
              </Box>

              {/* Add Extra Prop to Current Row */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'blue.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Add Extra Prop to Row</Typography>
                <Stack direction="row" spacing={1}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="select-key-label">Select Key</InputLabel>
                    <Select
                      labelId="select-key-label"
                      id="select-key"
                      value={selectedExtraPropKey}
                      onChange={(e) => {
                        console.log('Dropdown selection changed:', e.target.value);
                        setSelectedExtraPropKey(e.target.value);
                      }}
                      label="Select Key"
                      MenuProps={{
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            minWidth: 200,
                            zIndex: 9999,
                          },
                        },
                        MenuListProps: {
                          style: {
                            paddingTop: 0,
                            paddingBottom: 0,
                          },
                        },
                        disablePortal: true,
                      }}
                    >
                      {availableExtraPropKeys.length === 0 ? (
                        <MenuItem disabled value="">
                          No keys available - add some keys above first
                        </MenuItem>
                      ) : (
                        availableExtraPropKeys.map((key) => {
                          // Parse extra_props if it's a string
                          const extraPropsObj = typeof rowForProps?.extra_props === 'string'
                            ? JSON.parse(rowForProps.extra_props || '{}')
                            : (rowForProps?.extra_props || {});
                          const isAlreadyInRow = rowForProps?.extra_props && key in extraPropsObj;
                          return (
                            <MenuItem key={key} value={key} disabled={isAlreadyInRow}>
                              {key} {isAlreadyInRow ? '(already added)' : ''}
                            </MenuItem>
                          );
                        })
                      )}
                    </Select>
                  </FormControl>
                  <Button size="small" onClick={handleAddExtraPropToRow} disabled={!selectedExtraPropKey}>
                    Add to Row
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Available keys: {availableExtraPropKeys.length} |
                  Keys in current row: {rowForProps?.extra_props ? Object.keys(rowForProps.extra_props).length : 0}
                </Typography>
              </Box>

              {/* Current Row Extra Props */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Current Extra Props</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Key</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: 50 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowForProps && Object.entries(
                    typeof rowForProps.extra_props === 'string'
                      ? (() => {
                        try {
                          return JSON.parse(rowForProps.extra_props || '{}');
                        } catch (e) {
                          console.warn('Failed to parse extra_props JSON:', rowForProps.extra_props);
                          return {};
                        }
                      })()
                      : (rowForProps.extra_props || {})
                  ).map(([k, v]) => (
                    <TableRow key={k}>
                      <TableCell component="th" sx={{ pr: 2 }}>{k}</TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          variant="standard"
                          value={typeof v === 'string' ? v : JSON.stringify(v)}
                          onChange={(e) => {
                            // Bug 2: coerce "true"/"false"/numbers/JSON so a
                            // user-typed `true` lands as boolean, not the
                            // string "true".
                            const coerced = coerceExtraPropValue(e.target.value);
                            const updated = { ...(rowForProps.extra_props || {}), [k]: coerced };
                            handleExtraPropChange(rowForProps, updated);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Delete this extra prop">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteExtraProp(k)}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {rowForProps && Object.keys(rowForProps.extra_props || {}).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ textAlign: 'center', fontStyle: 'italic', color: 'text.secondary' }}>
                        No extra props added yet. Use "Add Extra Prop to Row" above to add some.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Drawer>
        </Box>
      </>
    );
  }

  // Fallback: nested array/object recordMode logic unchanged…
  // (copy your existing recordMode and object recursion here)
  return <RenderValue name={path[path.length - 1] || ''} value={data} onChange={() => { }} readOnly={readOnly} />;

}
